<template>
  <w-flex class="form-layout" align="stretch">
    <form-aside
      :tabs="tabs"
      :current-tab-name="selectedTab.name"
      :project-name="projectName"
      :document-title="documentTitle"
      :version="version"
      @change-tab="setTab"
    />

    <w-flex fluid direction="col" class="form-layout__body" align="stretch">
      <breadcrumbs-bar :breadcrumb-links="breadcrumbLinks" />

      <w-flex fluid class="form-layout__content w-scroll">
        <tab-container
          :active-tab="selectedTab"
          :tabs="tabs"
          :is-last-index="isLastIndex"
          :is-first-index="isFirstIndex"
          :number-of-steps="tabs.length"
          @fields-changed="setCurrentTabFields($event)"
          @next-step="nextTab"
          @prev-step="prevTab"
          @save="onSave"
        >
          <template v-for="slot in slots" #[slot]>
            <slot :name="slot">
              <div>No content provided for slot: {{ slot }}</div>
            </slot>
          </template>
        </tab-container>
      </w-flex>

      <actions-bar
        :is-super-admin="isSuperAdmin"
        :need-autosave="needAutosave"
        @save="onSave"
        @cancel="onCancel"
        @validate-all="onValidateAll"
      />
    </w-flex>
  </w-flex>
</template>

<script setup lang="ts">
import type { ComponentModel } from '@witkac/vue3-components/src/typings/componentModel';
import { onMounted, useSlots } from 'vue';

import { Theme } from '@/enums/theme.enum';
import type { BreadcrumbItem } from '@/typings/breadcrumbItem';
import type { Tab } from '@/typings/tab';

import ActionsBar from '../layoutComponents/ActionsBar.vue';
import BreadcrumbsBar from '../layoutComponents/BreadcrumbsBar.vue';
import FormAside from '../layoutComponents/FormAside.vue';
import TabContainer from '../tab/TabContainer.vue';

const props = defineProps<{
  breadcrumbLinks: Array<BreadcrumbItem>;
  offerId: number;
  version?: string;
  projectName?: string;
  needAutosave?: boolean;
  isFirstIndex: boolean;
  isLastIndex: boolean;
  isSuperAdmin?: boolean;
  tabs: Tab[];
  selectedTab: Tab;
  setCurrentTabFields: (fields: ComponentModel[]) => void;
  prevTab: () => any;
  nextTab: () => any;
  setTab: (Tab: Tab) => any;
  onSave: (leave: boolean) => any;
  onCancel: () => any;
  onValidateAll: () => any;
}>();

const currentTheme = Theme.Light;

onMounted(() => {
  document.body.classList.add(`w-theme--${currentTheme}`);
});

const documentTitle = props.breadcrumbLinks.find((x) =>
  x.Url.endsWith(props.offerId.toString()),
)?.Title;

const slots: any = Object.keys(useSlots());
</script>

<style lang="scss">
@font-face {
  font-family: 'Open Sans', sans-serif;
  src: url('/fonts/OpenSans.ttf') format('ttf');
}

body {
  font-family: 'Open Sans', sans-serif;
  font-weight: 350;
  letter-spacing: -0.01em;
  line-height: 1.45;
  padding: 0;
  margin: 0;
}

button,
a,
input {
  font-family: inherit;
}

.form-layout {
  height: 100vh;

  &__content {
    padding: 24px;
    background: w.color('background');
    height: 0;
    overflow: auto;
  }
}

.sb-show-main.sb-main-padded {
  padding: 0;
}
</style>
